import { useState } from "react";
import { BusyLabel } from "../components/loader/index.js";
import { useToast } from "../components/toast.js";
import { requestLoginLink, requestPasswordReset } from "../lib/api/index.js";

/**
 * Sign in with a link sent to your inbox, for an account that already exists.
 *
 * New accounts are still made only through Google (plans/google-auth-and-invites.md,
 * D1): the API sends a link only to an address it knows, and answers the same
 * either way. So does this form — saying "no account for that address" would
 * tell anyone which addresses have one.
 */

type State =
  { readonly kind: "idle" } | { readonly kind: "working" } | { readonly kind: "sent" };

/**
 * What the link is for. A forgotten password is the same emailed, single-use
 * link landing on a page that asks for the new password (asked for 2026-10-03,
 * when two people could not get back in).
 */
const PURPOSE = {
  "sign-in": {
    send: requestLoginLink,
    what: "a sign-in link",
    sent: "Sign-in link sent",
    failed: "Could not send a sign-in link",
    button: "Email me a sign-in link",
  },
  "password-reset": {
    send: requestPasswordReset,
    what: "a link to choose a new password",
    sent: "Link to choose a new password sent",
    failed: "Could not send the link",
    button: "Email me a link to choose a new password",
  },
} as const;

export function LinkForm({
  purpose = "sign-in",
}: {
  readonly purpose?: "sign-in" | "password-reset";
}) {
  const copy = PURPOSE[purpose];
  const [email, setEmail] = useState("");
  const [state, setState] = useState<State>({ kind: "idle" });
  const toast = useToast();

  if (state.kind === "sent") {
    return (
      <div aria-live="polite">
        <div className="auth-sent">
          If <strong>{email.trim()}</strong> has a Crux account, {copy.what} is on its
          way. It works <strong>once</strong> and expires in <strong>15 minutes</strong>;
          open it in this browser. No email in a minute? That address may not have an
          account yet: new accounts start with <strong>Continue with Google</strong>{" "}
          above.
        </div>
        <button
          type="button"
          className="button-link auth-password-open"
          onClick={() => {
            setState({ kind: "idle" });
          }}
        >
          Use a different address
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        setState({ kind: "working" });
        copy
          .send(email)
          .then(() => {
            setState({ kind: "sent" });
            toast.done(copy.sent);
          })
          .catch((error: unknown) => {
            setState({ kind: "idle" });
            toast.failed(copy.failed, error);
          });
      }}
    >
      <div className="auth-fields">
        <div className="auth-field">
          <label className="auth-label" htmlFor={`link-email-${purpose}`}>
            Email address
          </label>
          <input
            id={`link-email-${purpose}`}
            className="text-input"
            type="email"
            required
            autoFocus
            autoComplete="email"
            placeholder="you@company.com"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
            }}
          />
        </div>
      </div>
      <button
        className="button-primary auth-submit"
        type="submit"
        disabled={state.kind === "working"}
      >
        <BusyLabel
          busy={state.kind === "working"}
          idle={copy.button}
          working="Sending…"
        />
      </button>
    </form>
  );
}
